import { useRef, type MouseEvent, type Ref, type RefObject } from "react";
import classNames from "classnames";
import { CloseIcon } from "@icons";
import { markEventBlocked } from "@design-system/ViewTable/ViewTable.lib";
import {
    usePickerQuery,
    usePickerRoot,
    type PickerRootContextValue,
} from "./PickerContext";
import { usePickerClearSlotPosition } from "./usePickerClearSlotPosition";
import styles from "./PickerTriggerClear.module.scss";

/** Whether the root holds something a clear control can drop. */
export const hasClearableValue = ({
    root,
    inputValue,
}: {
    root: PickerRootContextValue;
    inputValue: string;
}) => !root.isDisabled && (root.selectedValues.length > 0 || inputValue !== "");

/**
 * Empties the root without opening it: selection to `null` / `[]` and, on
 * Combobox-kind roots, the typed text.
 */
export const clearPickerRoot = (root: PickerRootContextValue) => {
    root.setSelectedValues([]);
    root.setInputValue?.("");
};

/**
 * Clear control of the button triggers (Button element, cell). It is a sibling of the
 * Base UI trigger inside a group wrapper, never nested in it, so pressing it
 * neither opens the popup nor counts as a row click; the trigger reserves a
 * slot for it (`PickerTriggerClearSlot`) so the chevron never moves, and the
 * control is laid over that slot by measuring it. Hidden (but mounted) while
 * there is nothing to clear; focus moves back to the trigger once it hides.
 */
export const PickerTriggerClear = ({
    slotRef,
    triggerRef,
    className,
}: {
    /** The slot the trigger renders in its trailing cluster; the control sits over it. */
    slotRef: RefObject<HTMLSpanElement | null>;
    /** The sibling trigger button, which takes focus back once the control hides. */
    triggerRef: RefObject<HTMLButtonElement | null>;
    className?: string;
}) => {
    const root = usePickerRoot();
    const { inputValue } = usePickerQuery();
    const clearRef = useRef<HTMLButtonElement>(null);
    const isVisible = hasClearableValue({ root, inputValue });
    usePickerClearSlotPosition({ clearRef, slotRef, triggerRef });

    return (
        <button
            ref={clearRef}
            type="button"
            aria-label="Clear"
            aria-hidden={!isVisible || undefined}
            tabIndex={isVisible ? 0 : -1}
            className={classNames(
                styles.clear,
                { [styles.clearHidden]: !isVisible },
                className,
            )}
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                markEventBlocked(event);
                clearPickerRoot(root);
                triggerRef.current?.focus();
            }}
        >
            <CloseIcon aria-hidden />
        </button>
    );
};

/** The empty box a trigger keeps in its trailing cluster for the clear control. */
export const PickerTriggerClearSlot = ({
    ref,
}: {
    ref?: Ref<HTMLSpanElement>;
}) => <span ref={ref} className={styles.clearSlot} aria-hidden />;
